'use client';

import { useId, useRef, useState } from 'react';
import { cn } from '@savvy-kit/lib/utils';

import { DEFAULT_MAX_FILE_SIZE_BYTES } from './constants';
import DefaultDropZoneContent from './default-drop-zone-content';
import {
  buildAcceptAttribute,
  formatAllowedExtensionsPhrase,
  formatFileError,
  formatFileSize,
  getAllowedExtensionAndMimeSets,
  validateFiles,
} from './utils';

/**
 * **Why `id` exists:** `File.name` is not necessarily unique: user could select a file with the same name from different folders, and the browser doesn't give us access to the full path name for security reasons.
 */
export type FileDropZoneAcceptedFile = {
  /** Opaque id assigned when the file passes validation in this zone (do not persist as a server id). */
  readonly id: string;
  readonly file: File;
};

type FileDropZoneProps = {
  /** The content to render inside the file drop zone. Because the drop zone
   * is a button, children should not render buttons inside of it. To retain
   * the appearance of a button without violating accessibility rules, render
   * the button `asChild` and wrap its children in a span.
   */
  readonly children?: React.ReactNode;
  /**
   * Newly added files that passed type and size checks (parent merges into its list).
   * Each item includes a unique `id` — see {@link FileDropZoneAcceptedFile}.
   */
  readonly onFilesAdded: (files: FileDropZoneAcceptedFile[]) => void;
  readonly maxSizeBytes?: number;
  // Lowercase extensions without dots; aliases (e.g. `jpeg`) normalize to one canonical form (e.g. `jpg`).
  // Omit to accept any type, which also drops the `accept` filter from the picker. Size is still enforced.
  readonly allowedExtensions?: readonly string[];
  /** Called once per batch when any files fail size or type checks, with a formatted English message. */
  readonly onFileError?: (message: string) => void;
  /** Invoked once when a batch (drop or picker selection) begins processing (e.g. clear prior inline errors). */
  readonly onProcessFilesStart?: () => void;
  readonly multiple?: boolean;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly 'aria-label'?: string;
  /** Describes the button as an invalid entry and sets `data-invalid`; a button can't carry `aria-invalid`. */
  readonly 'aria-invalid'?: boolean;
  /** Forwarded to the trigger button so an external `<label htmlFor>` can name and click-forward to it. */
  readonly id?: string;
  readonly instructions?: string;
};

export function FileDropZone({
  children,
  onFilesAdded,
  maxSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
  allowedExtensions: allowedExtensionsProp,
  onFileError,
  onProcessFilesStart,
  multiple = true,
  disabled = false,
  className,
  'aria-label': ariaLabel = 'Upload files',
  'aria-invalid': ariaInvalid,
  id,
  instructions = '',
}: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const invalidDescriptionId = useId();
  const { allowedExtSet, allowedMimeSet, unrestricted } = getAllowedExtensionAndMimeSets(allowedExtensionsProp);
  const inputAcceptAttribute = buildAcceptAttribute(allowedExtSet);

  const maxSizeFormatted = formatFileSize(maxSizeBytes);
  const allowedExtensionsPhrase = formatAllowedExtensionsPhrase([...allowedExtSet]);

  function processFiles(incoming: File[]) {
    const files = multiple ? incoming : incoming.slice(0, 1);
    if (files.length === 0) {
      return;
    }

    onProcessFilesStart?.();

    const { acceptedFile, tooLargeFiles, filesOfDisallowedType } = validateFiles({
      files,
      maxSizeBytes,
      allowedExtSet,
      allowedMimeSet,
      unrestricted,
    });

    if (tooLargeFiles.length > 0 || filesOfDisallowedType.length > 0) {
      onFileError?.(
        formatFileError({
          files: {
            tooLarge: tooLargeFiles,
            disallowed: filesOfDisallowedType,
          },
          info: {
            maxSizeFormatted,
            allowedExtensionsPhrase,
          },
        }),
      );
    }
    if (acceptedFile.length > 0) {
      // Unique id per row: see FileDropZoneAcceptedFile JSDoc.
      onFilesAdded(
        acceptedFile.map((file) => ({
          id: crypto.randomUUID(),
          file,
        })),
      );
    }
  }

  function dragEventWrapper(e: React.DragEvent, fn: (e: React.DragEvent) => void) {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) {
      return;
    }

    fn(e);
  }

  function handleDragEnter(e: React.DragEvent) {
    dragEventWrapper(e, () => {
      setIsDragging(true);
    });
  }

  function handleDragLeave(e: React.DragEvent) {
    dragEventWrapper(e, (e) => {
      if (e.currentTarget === e.target) {
        setIsDragging(false);
      }
    });
  }

  function handleDragOver(e: React.DragEvent) {
    dragEventWrapper(e, () => {
      setIsDragging(true);
    });
  }

  function handleDrop(e: React.DragEvent) {
    dragEventWrapper(e, () => {
      setIsDragging(false);

      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length > 0) {
        processFiles(dropped);
      }
    });
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files;

    if (selected && selected.length > 0) {
      processFiles(Array.from(selected));
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function handleZoneClick() {
    if (disabled) {
      return;
    }

    inputRef.current?.click();
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={inputAcceptAttribute}
        multiple={multiple}
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled}
        aria-hidden
      />
      <button
        type="button"
        onClick={handleZoneClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        disabled={disabled}
        id={id}
        aria-label={id === undefined ? ariaLabel : undefined}
        aria-describedby={ariaInvalid === true ? invalidDescriptionId : undefined}
        data-invalid={ariaInvalid === true ? '' : undefined}
        className={cn(
          'cursor-pointer p-6 bg-card',
          'rounded-lg border border-dashed border-border-light transition-colors',
          'hover:border-border hover:bg-muted ',
          disabled && 'cursor-not-allowed opacity-60',
          className,
          isDragging && 'border-primary bg-muted',
        )}
      >
        {children ?? <DefaultDropZoneContent instructions={instructions} />}
        {ariaInvalid === true ? (
          <span id={invalidDescriptionId} hidden>
            Invalid entry
          </span>
        ) : null}
      </button>
    </>
  );
}
